import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Eye, X, RotateCcw, Trash2, CheckCircle, Clock } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { formatDateTime } from '../../utils/timeUtils';
import OrderList from '../../components/OrderList';
import { OrderFilterExt } from '../../hooks/useOrders';
import AuthConfirmModal from '../../components/AuthConfirmModal';

const REFUND_DESTINATION_LABELS: Record<string, string> = {
  original: '原路退回',
  balance: '退回平台余额',
};

export default function TeacherOrders() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // 弹窗状态
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [showRefundDetailModal, setShowRefundDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [refundForm, setRefundForm] = useState({ amount: 0, destination: 'original' as 'original' | 'balance', reason: '' });

  // 统计数据（独立查询）
  const [stats, setStats] = useState({ total: 0, pending: 0, paid: 0, refunded: 0, cancelled: 0, revenue: 0 });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    const { data, error } = await supabase
      .from('orders')
      .select('status, actual_price, price')
      .eq('role', 'teacher');

    if (error) return;

    const orders = data || [];
    setStats({
      total: orders.length,
      pending: orders.filter(o => o.status === 'pending').length,
      paid: orders.filter(o => o.status === 'paid').length,
      refunded: orders.filter(o => o.status === 'refunded').length,
      cancelled: orders.filter(o => (o.status as string) === 'cancelled').length,
      revenue: orders.filter(o => o.status === 'paid').reduce((sum, o) => sum + (o.actual_price != null ? Number(o.actual_price) : Number(o.price) || 0), 0),
    });
  };

  const filterExt: OrderFilterExt = useMemo(() => ({
    search: searchQuery,
    status: statusFilter === 'all' ? undefined : statusFilter,
    dateStart: dateRange.start || undefined,
    dateEnd: dateRange.end || undefined,
  }), [searchQuery, statusFilter, dateRange]);

  // 关闭教师套餐的辅助函数
  const closeTeacherPackage = async (packageId: string | undefined) => {
    if (!packageId) return;
    try {
      await supabase
        .from('user_packages')
        .update({ is_active: false })
        .eq('id', packageId)
        .eq('role', 'teacher');
    } catch (err) {
      console.error('关闭教师套餐失败:', err);
    }
  };

  // 退款操作
  const openRefundModal = (order: any) => {
    setSelectedOrder(order);
    setRefundForm({
      amount: order.actualPrice != null ? Number(order.actualPrice) : Number(order.originalPrice),
      destination: 'original',
      reason: '',
    });
    setShowRefundModal(true);
  };

  const handleRefund = async () => {
    if (!selectedOrder) {
      alert('未选择订单');
      return;
    }
    if (!refundForm.reason.trim()) {
      alert('请输入退款原因');
      return;
    }

    // 判断是否为无实际支付的订单（后台开通/券码兑换）
    const isNoPaymentOrder = selectedOrder.payMethod === 'admin' || selectedOrder.payMethod === 'coupon';

    if (!isNoPaymentOrder && refundForm.amount <= 0 && !(selectedOrder.pointsDeducted && selectedOrder.pointsDeducted > 0)) {
      alert('退款金额必须大于0');
      return;
    }

    try {
      const session = (await supabase.auth.getSession()).data.session;

      // 幂等性检查：先查询订单当前状态，防止重复退款
      const { data: currentOrder } = await supabase
        .from('orders')
        .select('status, refund_status')
        .eq('id', selectedOrder.id)
        .maybeSingle();

      if (currentOrder?.status === 'refunded' || currentOrder?.refund_status === 'refunded') {
        alert('该订单已退款，请勿重复操作');
        setShowRefundModal(false);
        setSelectedOrder(null);
        return;
      }

      if (!isNoPaymentOrder) {
        // 1. 处理教师U币抵扣退回
        if (selectedOrder.pointsDeducted && selectedOrder.pointsDeducted > 0) {
          try {
            const pointsRefundRes = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify({
                action: 'add_points',
                user_id: selectedOrder.userId,
                role: 'teacher',
                points: selectedOrder.pointsDeducted,
                point_type: 'activity_reward',
                source_type: 'refund',
                source_id: selectedOrder.id,
                source_detail: { order_no: selectedOrder.orderNo, original_deduction: selectedOrder.pointsDeducted },
                remark: `订单退款退回U币: 订单${selectedOrder.orderNo}, 退回${selectedOrder.pointsDeducted}U币`,
              }),
            });
            const pointsRefundData = await pointsRefundRes.json();
            if (!pointsRefundData.success) {
              alert('U币退回失败: ' + (pointsRefundData.error || '未知错误'));
              return;
            }
          } catch (e) {
            alert('U币退回接口调用失败');
            return;
          }
        }

        // 2. 处理支付宝退款
        const needAlipayRefund = refundForm.destination === 'original' &&
          (selectedOrder.payMethod === 'alipay' || selectedOrder.payMethod === 'points_alipay');

        if (needAlipayRefund) {
          const alipayRefundAmount = selectedOrder.payMethod === 'points_alipay'
            ? (Number(selectedOrder.actualPrice) - (selectedOrder.deductionAmount || 0))
            : refundForm.amount;

          if (alipayRefundAmount > 0) {
            const refundRes = await fetch(`${getSupabaseUrl()}/functions/v1/alipay-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session?.access_token}`,
              },
              body: JSON.stringify({
                action: 'createRefund',
                orderId: selectedOrder.id,
                refundAmount: alipayRefundAmount,
                refundReason: refundForm.reason,
                orderType: 'teacher',
              }),
            });
            const refundData = await refundRes.json();
            if (!refundData.success) {
              alert('支付宝退款失败: ' + (refundData.error || '未知错误'));
              return;
            }
          }
        }
      }

      const { error } = await supabase.from('orders').update({
        status: 'refunded',
        refund_status: 'refunded',
        refund_amount: isNoPaymentOrder ? 0 : refundForm.amount,
        refund_destination: isNoPaymentOrder ? 'none' : refundForm.destination,
        refund_reason: refundForm.reason,
        refunded_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).eq('id', selectedOrder.id).eq('role', 'teacher');

      if (error) {
        alert('退款失败: ' + error.message);
        return;
      }

      await closeTeacherPackage(selectedOrder.packageId);

      // 发送通知
      try {
        const refundContent = isNoPaymentOrder
          ? `您的订单【${selectedOrder.orderNo}】已关闭，套餐已停用。如有疑问请联系客服。`
          : `您的订单【${selectedOrder.orderNo}】已成功退款。${selectedOrder.pointsDeducted && selectedOrder.pointsDeducted > 0 ? `已退回${selectedOrder.pointsDeducted}U币至您的账户。` : ''}${refundForm.amount > 0 ? `退款金额：¥${refundForm.amount.toFixed(2)}，退款去向：${refundForm.destination === 'original' ? '原路退回' : '平台余额'}。` : ''}如有疑问请联系客服。`;
        await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            action: 'messages_send_notification',
            userId: selectedOrder.userId,
            title: isNoPaymentOrder ? '套餐关闭通知' : '订单退款通知',
            content: refundContent,
          }),
        });
      } catch (e) {
        // ignore
      }

      setShowRefundModal(false);
      setSelectedOrder(null);
      loadStats();
      alert(isNoPaymentOrder ? '套餐已关闭' : '退款成功');

      // 触发订单更新事件，通知 OrderList 组件刷新列表
      window.dispatchEvent(new Event('order-updated'));
    } catch (err) {
      alert('退款异常: ' + (err as Error).message);
    }
  };

  // 删除操作
  const openDeleteModal = (order: any) => {
    setSelectedOrder(order);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedOrder) return;
    try {
      await closeTeacherPackage(selectedOrder.packageId);
      const { error } = await supabase.from('orders').delete().eq('id', selectedOrder.id).eq('role', 'teacher');
      if (error) {
        alert('删除失败: ' + error.message);
        return;
      }
      setShowDeleteModal(false);
      setSelectedOrder(null);
      loadStats();

      // 触发订单更新事件，通知 OrderList 组件刷新列表
      window.dispatchEvent(new Event('order-updated'));
    } catch (err) {
      alert('删除异常: ' + (err as Error).message);
    }
  };

  // 查看退款详情
  const openRefundDetail = (order: any) => {
    setSelectedOrder(order);
    setShowRefundDetailModal(true);
  };

  // 包装批量删除：先存ids再弹确认框
  const [pendingBatchIds, setPendingBatchIds] = useState<string[]>([]);
  const handleBatchDeleteWithConfirm = (ids: string[]) => {
    setPendingBatchIds(ids);
    setShowBatchDeleteModal(true);
  };

  const confirmBatchDeleteFinal = async () => {
    try {
      const { data: ordersToDelete } = await supabase.from('orders').select('id, package_id').in('id', pendingBatchIds).eq('role', 'teacher');
      if (ordersToDelete) {
        for (const order of ordersToDelete) {
          await closeTeacherPackage(order.package_id);
        }
      }
      const { error } = await supabase.from('orders').delete().in('id', pendingBatchIds).eq('role', 'teacher');
      if (error) {
        alert('批量删除失败: ' + error.message);
        return;
      }
      setPendingBatchIds([]);
      setShowBatchDeleteModal(false);
      loadStats();
      alert('批量删除成功');

      // 触发订单更新事件，通知 OrderList 组件刷新列表
      window.dispatchEvent(new Event('order-updated'));
    } catch (err) {
      alert('批量删除异常: ' + (err as Error).message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">教师订单</h1>
          <p className="text-gray-500 mt-1">查看教师购买套餐的订单记录</p>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">总订单数</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">待支付</div>
          <div className="text-2xl font-bold text-yellow-600 mt-1">{stats.pending}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">已支付</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{stats.paid}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">已退款</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{stats.refunded}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">已关闭</div>
          <div className="text-2xl font-bold text-gray-600 mt-1">{stats.cancelled}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="text-sm text-gray-500">总收入</div>
          <div className="text-2xl font-bold text-purple-600 mt-1">¥{stats.revenue.toFixed(2)}</div>
        </div>
      </div>

      {/* 搜索筛选 */}
      <div className="bg-white rounded-t-xl border-x border-t border-gray-200 p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" placeholder="搜索订单号、教师姓名、手机号..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg outline-none">
            <option value="all">全部状态</option>
            <option value="pending">待支付</option>
            <option value="paid">已支付</option>
            <option value="refunded">已退款</option>
            <option value="cancelled">已关闭</option>
          </select>
          <input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} className="px-3 py-2 border border-gray-200 rounded-lg outline-none" placeholder="开始时间" />
          <input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} className="px-3 py-2 border border-gray-200 rounded-lg outline-none" placeholder="结束时间" />
        </div>
      </div>

      {/* 订单列表 */}
      <OrderList
        type="admin-all-teacher"
        mode="page"
        filterExt={filterExt}
        showExport
        exportFileName="教师订单"
        showBatchSelect
        onBatchDelete={handleBatchDeleteWithConfirm}
        onRefund={openRefundModal}
        onDelete={openDeleteModal}
        onViewRefundDetail={openRefundDetail}
      />

      {/* 退款详情弹窗 */}
      <AnimatePresence>
        {showRefundDetailModal && selectedOrder && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowRefundDetailModal(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Eye className="w-5 h-5 text-blue-500" />退款详情</h3>
                <button onClick={() => setShowRefundDetailModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">订单号</span><span className="font-mono">{selectedOrder.orderNo}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">教师</span><span>{selectedOrder.teacherName || '-'}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">实付金额</span><span className="text-gray-800">¥{Number(selectedOrder.actualPrice || 0).toFixed(2)}</span></div>
                </div>
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center"><CheckCircle className="w-4 h-4 text-green-600" /></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">退款申请已处理</div>
                      <div className="text-xs text-gray-500">{formatDateTime(selectedOrder.refundedAt)}</div>
                    </div>
                  </div>
                  <div className="ml-4 pl-4 border-l-2 border-green-200 space-y-3">
                    <div className="flex justify-between text-sm"><span className="text-gray-500">退款金额</span><span className="font-bold text-red-600">¥{Number(selectedOrder.refundAmount || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-500">退款去向</span><span className="text-gray-800">{REFUND_DESTINATION_LABELS[selectedOrder.refundDestination || 'original'] || '-'}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-500">退款原因</span><span className="text-gray-800 max-w-[200px] text-right">{selectedOrder.refundReason || '-'}</span></div>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center"><Clock className="w-4 h-4 text-blue-600" /></div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">退款到账中</div>
                      <div className="text-xs text-gray-500">预计1-3个工作日到账</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 批量删除确认弹窗 */}
      <AnimatePresence>
        {showBatchDeleteModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-500" />确认批量删除</h3>
                <button onClick={() => setShowBatchDeleteModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-red-700 text-sm">删除后选中的 {pendingBatchIds.length} 条订单将彻底删除。此操作不可恢复。</p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowBatchDeleteModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">取消</button>
                  <button onClick={confirmBatchDeleteFinal} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">确认删除</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 退款弹窗 */}
      <AnimatePresence>
        {showRefundModal && selectedOrder && (() => {
          const isNoPayment = selectedOrder.payMethod === 'admin' || selectedOrder.payMethod === 'coupon';
          return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><RotateCcw className="w-5 h-5 text-orange-500" />{isNoPayment ? '关闭套餐' : '订单退款'}</h3>
                <button onClick={() => setShowRefundModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
              </div>
              <div className="p-4 space-y-4">
                {isNoPayment && (
                  <div className="bg-yellow-50 rounded-lg p-3 text-sm text-yellow-700">
                    此订单为{selectedOrder.payMethod === 'admin' ? '后台开通' : '券码兑换'}，无实际支付，退款将仅关闭套餐。
                  </div>
                )}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">订单号</span><span className="font-mono">{selectedOrder.orderNo}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">教师</span><span>{selectedOrder.teacherName || '-'}</span></div>
                  {!isNoPayment && (
                    <>
                      <div className="border-t border-gray-200 my-2"></div>
                      <div className="flex justify-between text-sm"><span className="text-gray-500">订单总额</span><span>¥{Number(selectedOrder.originalPrice || 0).toFixed(2)}</span></div>
                      {selectedOrder.pointsDeducted && selectedOrder.pointsDeducted > 0 && (
                        <div className="flex justify-between text-sm text-orange-600">
                          <span>支出</span>
                          <span>-{selectedOrder.pointsDeducted}分 (约¥{(selectedOrder.deductionAmount || 0).toFixed(2)})</span>
                        </div>
                      )}
                      <div className="border-t border-gray-200 my-2"></div>
                      <div className="flex justify-between text-sm font-semibold">
                        <span className="text-gray-700">实付金额</span>
                        <span className="text-lg font-bold text-blue-600">¥{Number(selectedOrder.actualPrice || 0).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>
                {!isNoPayment && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">退款金额</label>
                    <input type="number" value={refundForm.amount} onChange={e => setRefundForm({...refundForm, amount: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 border rounded-lg" min={0} max={selectedOrder.actualPrice} />
                  </div>
                )}
                {!isNoPayment && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">退款去向</label>
                    <select value={refundForm.destination} onChange={e => setRefundForm({...refundForm, destination: e.target.value as 'original' | 'balance'})} className="w-full px-3 py-2 border rounded-lg">
                      <option value="original">原路退回</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{isNoPayment ? '关闭原因' : '退款说明'}</label>
                  <textarea value={refundForm.reason} onChange={e => setRefundForm({...refundForm, reason: e.target.value})} placeholder={isNoPayment ? '请输入关闭原因...' : '请输入退款原因...'} rows={3} className="w-full px-3 py-2 border rounded-lg resize-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setShowRefundModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">取消</button>
                  <button onClick={handleRefund} disabled={!refundForm.reason.trim() || (!isNoPayment && refundForm.amount <= 0 && !(selectedOrder.pointsDeducted && selectedOrder.pointsDeducted > 0))} className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50">{isNoPayment ? '确认关闭' : '确认退款'}</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* 删除确认弹窗 */}
      <AnimatePresence>
        {showDeleteModal && selectedOrder && (
          <AuthConfirmModal
            isOpen={showDeleteModal}
            title="确认删除订单"
            message={`确定要删除订单"${selectedOrder.orderNo}"吗？删除后该订单将彻底删除，此操作不可恢复。`}
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
